'use client';

import * as React from 'react';
import { useFormContext } from 'react-hook-form';

import { useSidebar } from '@workspace/ui/components/sidebar';

import { GENERATOR_STEP_CONFIG_MAP } from '~/components/generator/core/generator-step-config';
import { NextButton } from '~/components/generator/shared/next-button';
import type { CompleteGeneratorSchema } from '~/schemas/generator/complete-generator-schema';
import { GeneratorStep } from '~/schemas/generator/complete-generator-schema';

export interface WizardFooterProps {
  canNext: boolean;
  loading: boolean;
  isLastStep: boolean;
  handleNext: () => void;
  currentStep: GeneratorStep;
}

export function WizardFooter({
  canNext,
  loading,
  isLastStep,
  handleNext,
  currentStep
}: WizardFooterProps): React.JSX.Element {
  const methods = useFormContext<CompleteGeneratorSchema>();

  // Get sidebar state to calculate proper positioning
  let sidebar = null;
  try {
    sidebar = useSidebar();
  } catch {
    // If not within sidebar context (e.g., embed page), sidebar remains null
  }

  // Get step-specific data and configuration
  const stepConfig = React.useMemo(() => {
    const config =
      GENERATOR_STEP_CONFIG_MAP[
        currentStep as keyof typeof GENERATOR_STEP_CONFIG_MAP
      ];
    if (!config) return null;

    const selectedItems = methods.watch(config.fieldPath) || [];

    const onItemRemove = (id: string) => {
      const currentItems = methods.getValues(config.fieldPath) || [];
      const newItems = currentItems.filter((item: any) => item.id !== id);
      methods.setValue(config.fieldPath, newItems as any, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true
      });
    };

    return {
      selectedItems,
      onItemRemove,
      ...config
    };
  }, [currentStep, methods]);

  // Calculate left position based on sidebar state
  const leftPosition = React.useMemo(() => {
    if (!sidebar || sidebar.isMobile) {
      return '0px'; // Full width on mobile or when no sidebar
    }
    return sidebar.state === 'collapsed'
      ? 'var(--sidebar-width-icon)'
      : 'var(--sidebar-width)';
  }, [sidebar]);

  if (!stepConfig) {
    return (
      <div
        className="fixed bottom-0 right-0 border-t border-border bg-background py-4 z-10"
        style={{ left: leftPosition }}
      >
        <div className="flex items-center justify-end">
          <NextButton
            loading={loading}
            disabled={!canNext}
            isLastStep={isLastStep}
            onClick={handleNext}
            className="mt-0"
          />
        </div>
      </div>
    );
  }

  const {
    selectedItems,
    SelectedItemCardComponent,
    selectedItemsIcon: SelectedItemsIcon,
    selectedItemsLabel,
    onItemRemove
  } = stepConfig;

  return (
    <div
      className="fixed bottom-0 right-0 border-t border-border bg-background p-4 z-10"
      style={{ left: leftPosition }}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="hidden items-center gap-2 md:flex">
            <SelectedItemsIcon className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{selectedItemsLabel}</span>
          </div>
          <div className="flex gap-2 transition-all duration-300 ease-in-out">
            {selectedItems.length > 0 ? (
              selectedItems.map((item) => (
                <div
                  key={item.id}
                  data-item-id={item.id}
                  className="animate-in slide-in-from-left-2 transition-all duration-300 ease-in-out"
                >
                  <SelectedItemCardComponent
                    item={item as any}
                    onRemove={onItemRemove}
                  />
                </div>
              ))
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-dashed border-muted-foreground/30 text-muted-foreground md:h-18 md:w-18">
                <div className="flex flex-col items-center gap-1 text-center">
                  <span className="text-xs">
                    None
                    <br />
                    selected
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NextButton
            loading={loading}
            disabled={!canNext}
            isLastStep={isLastStep}
            onClick={handleNext}
            className="mt-0"
          />
        </div>
      </div>
    </div>
  );
}
